import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowDown, ArrowRight, ArrowUpRight, Check, CircleDot, FileText, Gauge,
  Globe2, Headphones, Layers3, Link as LinkIcon, RefreshCw, Settings,
  ShieldCheck, ShoppingCart, Smartphone, Sparkles, Apple, Zap,
} from "lucide-react";
import {
  BRAND, COMPLIANCE_POINTS, HERO_STATS, PROCESS_STEPS, SERVICES, WHY_CHOOSE_FAQ,
  servicePath, serviceSlug,
} from "../lib/constants";

const SERVICE_ICONS = {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple, Headphones, Settings, Link: LinkIcon,
};

const STACK = [
  { icon: Layers3, label: "Broker CRM", note: "Clients, partners, operations" },
  { icon: Zap, label: "Liquidity", note: "Routing and connectivity" },
  { icon: ShieldCheck, label: "Risk & compliance", note: "Controls and verification" },
  { icon: Globe2, label: "Digital experience", note: "Web and mobile products" },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

function Eyebrow({ children, dark = false }) {
  return (
    <div className={`mb-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] ${dark ? "text-[#07110C]/65" : "text-[#D6B86A]"}`}>
      <span className={`h-2 w-2 rounded-full ${dark ? "bg-[#174D3A]" : "bg-[#D6B86A]"}`} /> {children}
    </div>
  );
}

export default function HomePage() {
  return (
    <>
      <section id="home" className="relative min-h-[calc(100vh-92px)] overflow-hidden bg-[#07110C]">
        <div className="absolute inset-0 xpert-grid opacity-60" />
        <div className="absolute -left-40 top-0 h-[620px] w-[620px] rounded-full bg-[#174D3A]/45 blur-[160px]" />
        <div className="relative mx-auto grid min-h-[calc(100vh-92px)] max-w-7xl items-center gap-14 px-6 py-14 lg:grid-cols-[1.05fr_.95fr] lg:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#C8FF4D]/25 bg-[#C8FF4D]/[0.06] px-4 py-2">
              <CircleDot className="h-3.5 w-3.5 text-[#C8FF4D]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.23em] text-[#C8FF4D]">{BRAND.tagline}</span>
            </div>
            <h1 className="max-w-3xl font-display text-[3.4rem] font-bold leading-[.92] tracking-[-.065em] text-white sm:text-[5rem] lg:text-[6rem]">
              Build a broker.
              <span className="mt-2 block text-[#D6B86A]">Not a patchwork.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/58 sm:text-lg">
              One expert partner for CRM, liquidity, prop-firm systems, websites, mobile apps,
              and the integrations that keep your operation moving.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#C8FF4D] px-7 py-4 font-extrabold text-[#07110C]">
                Plan your platform <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href="#capabilities" className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 px-7 py-4 font-bold text-white">
                Explore capabilities <ArrowDown className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-12 grid max-w-xl grid-cols-3 border-t border-white/10">
              {HERO_STATS.map((stat) => (
                <div key={stat.label} className="border-r border-white/10 py-5 pr-4 last:border-0">
                  <div className="font-display text-2xl font-bold text-white sm:text-3xl">{stat.value}</div>
                  <div className="mt-1 text-[9px] uppercase tracking-[.16em] text-white/35">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
            <div className="rounded-[2rem] border border-white/10 bg-[#0B1711]/90 p-4 shadow-[0_40px_120px_rgba(0,0,0,.45)] sm:p-6">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-[.24em] text-white/35">System overview</span>
                  <h2 className="mt-1 font-display text-xl font-bold text-white">Broker command layer</h2>
                </div>
                <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-[#C8FF4D]">
                  <i className="h-2 w-2 rounded-full bg-[#C8FF4D] shadow-[0_0_12px_#C8FF4D]" /> Online
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {STACK.map(({ icon: Icon, label, note }) => (
                  <div key={label} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5">
                    <div className="flex items-start justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#C8FF4D]/10 text-[#C8FF4D]"><Icon className="h-5 w-5" /></span>
                      <Check className="h-4 w-4 text-[#C8FF4D]" />
                    </div>
                    <h3 className="mt-5 font-display font-bold text-white">{label}</h3>
                    <p className="mt-1 text-xs text-white/38">{note}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-[#C8FF4D] p-5 text-[#07110C]">
                <div><span className="font-mono text-[9px] uppercase tracking-widest opacity-50">Architecture health</span><strong className="mt-1 block font-display text-xl">Ready to scale</strong></div>
                <Gauge className="h-10 w-10" strokeWidth={1.5} />
              </div>
            </div>
            <div className="absolute -inset-4 -z-10 rotate-3 rounded-[2.3rem] border border-[#C8FF4D]/10" />
          </motion.div>
        </div>
      </section>

      <section id="capabilities" className="bg-[#F3F5ED] py-24 text-[#07110C] sm:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...reveal} className="grid gap-8 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <div>
              <Eyebrow>Digital services</Eyebrow>
              <h2 className="font-display text-4xl font-bold leading-[1] tracking-[-.05em] sm:text-6xl">Eight capabilities.<br />One standard.</h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-[#07110C]/60 lg:justify-self-end">
              Use one service or combine several into a connected delivery program. Every engagement
              is planned around the same business goals, technology principles, and client experience.
            </p>
          </motion.div>

          <div className="mt-16 border-t border-[#07110C]/15">
            {SERVICES.map((service, index) => {
              const Icon = SERVICE_ICONS[service.icon] || Sparkles;
              return (
                <motion.div key={service.title} {...reveal}>
                  <Link
                    to={servicePath(serviceSlug(service.title))}
                    className="group grid gap-5 border-b border-[#07110C]/15 py-7 transition-all hover:bg-[#D6B86A] sm:grid-cols-[64px_56px_1fr_1fr_48px] sm:items-center sm:px-4"
                  >
                    <span className="font-mono text-xs text-[#07110C]/35">0{index + 1}</span>
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#07110C]/15"><Icon className="h-5 w-5" /></span>
                    <h3 className="font-display text-xl font-bold sm:text-2xl">{service.title}</h3>
                    <p className="text-sm leading-relaxed text-[#07110C]/55 group-hover:text-[#07110C]/70">{service.desc}</p>
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="fx" className="overflow-hidden bg-[#07110C] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...reveal} className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>Flagship broker CRM</Eyebrow>
              <h2 className="font-display text-4xl font-bold leading-[1] tracking-[-.05em] sm:text-6xl">
                One view of clients.<br /><span className="text-[#C8FF4D]">One place to act.</span>
              </h2>
              <p className="mt-7 max-w-lg leading-relaxed text-white/52">
                Bring onboarding, wallets, partner networks, support, and risk signals into a
                workspace your teams can understand and your clients can trust.
              </p>
              <Link to="/solutions/forex-crm" className="mt-8 inline-flex items-center gap-3 font-bold text-[#C8FF4D]">
                Explore the CRM <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-[2rem] bg-[#174D3A] p-5 sm:p-8">
                <div className="h-full rounded-2xl border border-white/10 bg-[#0B1711] p-5">
                  <div className="flex items-center justify-between"><span className="font-display font-bold">Operations today</span><span className="rounded-full bg-[#C8FF4D]/10 px-3 py-1 font-mono text-[9px] text-[#C8FF4D]">LIVE</span></div>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    {["New clients", "KYC review", "Withdrawals"].map((label, i) => (
                      <div key={label} className="rounded-xl bg-white/[0.04] p-4"><strong className="font-display text-2xl">{[128, 16, 42][i]}</strong><span className="mt-2 block text-[9px] uppercase tracking-wider text-white/35">{label}</span></div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    {[74, 56, 88, 42].map((width, i) => <div key={i} className="rounded-xl border border-white/[0.06] p-3"><div className="h-2 rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-[#C8FF4D]" style={{ width: `${width}%` }} /></div></div>)}
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 rounded-2xl bg-[#C8FF4D] px-6 py-5 text-[#07110C] shadow-xl"><strong className="font-display text-2xl">360°</strong><span className="ml-2 text-xs font-bold uppercase tracking-wider">client view</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#DCE5D4] py-24 text-[#07110C] sm:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...reveal} className="grid gap-12 lg:grid-cols-[.75fr_1.25fr]">
            <div>
              <Eyebrow dark>Our approach</Eyebrow>
              <h2 className="font-display text-4xl font-bold leading-[1] tracking-[-.05em] sm:text-6xl">Complex work.<br />Clear process.</h2>
            </div>
            <div className="border-t border-[#07110C]/20">
              {PROCESS_STEPS.map((step) => (
                <div key={step.num} className="grid gap-4 border-b border-[#07110C]/20 py-7 sm:grid-cols-[70px_180px_1fr] sm:items-start">
                  <span className="font-mono text-xs opacity-40">{step.num}</span>
                  <h3 className="font-display text-xl font-bold">{step.title}</h3>
                  <p className="text-sm leading-relaxed opacity-60">{step.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#F3F5ED] py-24 text-[#07110C] sm:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...reveal} className="text-center">
            <Eyebrow>Why FX Broker Xpert</Eyebrow>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-bold leading-[1] tracking-[-.05em] sm:text-6xl">Less vendor friction.<br />More forward motion.</h2>
          </motion.div>
          <div className="mt-16 grid gap-px overflow-hidden rounded-[2rem] bg-[#07110C]/10 md:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE_FAQ.map((item, index) => (
              <motion.div key={item.q} {...reveal} className={`bg-[#F3F5ED] p-8 sm:p-10 ${index === 0 ? "lg:col-span-2" : ""}`}>
                <span className="font-mono text-[10px] text-[#07110C]/30">0{index + 1}</span>
                <h3 className="mt-8 max-w-xl font-display text-xl font-bold leading-snug sm:text-2xl">{item.q}</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#07110C]/55">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B1711] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...reveal} className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Eyebrow>Compliance-aware delivery</Eyebrow>
              <h2 className="font-display text-4xl font-bold leading-[1] tracking-[-.05em] sm:text-6xl">Trust belongs in the architecture.</h2>
              <p className="mt-7 max-w-lg leading-relaxed text-white/50">Connect identity, screening, permissions, and review steps to daily workflows without turning compliance into a disconnected obstacle.</p>
            </div>
            <div className="space-y-3">
              {COMPLIANCE_POINTS.map((point, index) => (
                <div key={point.title} className="flex gap-5 rounded-2xl border border-white/10 p-6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C8FF4D] font-mono text-xs font-bold text-[#07110C]">0{index + 1}</span>
                  <div><h3 className="font-display text-lg font-bold">{point.title}</h3><p className="mt-2 text-sm leading-relaxed text-white/45">{point.desc}</p></div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section id="contact" className="bg-[#F3F5ED] py-24 text-[#07110C] sm:py-32">
        <motion.div {...reveal} className="mx-auto max-w-7xl px-6">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-[#174D3A] p-8 text-white sm:p-14 lg:p-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full border-[60px] border-[#C8FF4D]/10" />
            <div className="relative max-w-4xl">
              <Eyebrow>Start here</Eyebrow>
              <h2 className="font-display text-4xl font-bold leading-[.98] tracking-[-.055em] sm:text-6xl lg:text-7xl">Your next broker milestone starts with a clear plan.</h2>
              <p className="mt-7 max-w-xl leading-relaxed text-white/55">Share what you want to launch, replace, or improve. We&apos;ll help identify the right first move.</p>
              <Link to="/contact" className="mt-9 inline-flex items-center gap-2 rounded-2xl bg-[#D6B86A] px-7 py-4 font-extrabold text-[#07110C]">
                Start a conversation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}
